/** Pretty-log annual energy buckets shared by the public HD map and GridPlan combined map. */

import { formatEnergyKwh } from './formatEnergyUnits';

export const ENERGY_RAMP_COLORS = [
    { rgba: [136, 19, 55, 235], hex: '#881337' },
    { rgba: [220, 38, 38, 225], hex: '#dc2626' },
    { rgba: [234, 88, 12, 215], hex: '#ea580c' },
    { rgba: [101, 163, 13, 205], hex: '#65a30d' },
    { rgba: [13, 148, 136, 200], hex: '#0d9488' },
    { rgba: [37, 99, 235, 190], hex: '#2563eb' },
    { rgba: [30, 58, 138, 185], hex: '#1e3a8a' },
];

export const MIN_THRESHOLD_KWH = 1000;
export const EMPTY_HEX_COLOR = [156, 163, 175, 100];

function prettyLogCeiling(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return MIN_THRESHOLD_KWH;
    const exp = Math.floor(Math.log10(n));
    const base = 10 ** exp;
    const f = n / base;
    const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
    return nice * base;
}

function prettyLogSequence(minVal, maxVal, mantissas) {
    const lo = Math.max(minVal, MIN_THRESHOLD_KWH);
    const hi = prettyLogCeiling(Math.max(maxVal, lo));
    const expStart = Math.floor(Math.log10(lo));
    const expEnd = Math.floor(Math.log10(hi));
    const out = [];
    for (let exp = expStart; exp <= expEnd + 1; exp += 1) {
        const scale = 10 ** exp;
        for (const mantissa of mantissas) {
            const value = mantissa * scale;
            if (value >= lo * 0.999 && value <= hi * 1.001) out.push(value);
        }
    }
    const unique = [...new Set(out.map((v) => Number(v.toPrecision(12))))].sort((a, b) => a - b);
    if (!unique.length || unique[0] > lo) unique.unshift(lo);
    if (unique[unique.length - 1] < hi) unique.push(hi);
    return unique;
}

function prettyLogBreaks(minVal, maxVal, targetCount) {
    const wanted = Math.max(2, targetCount);
    let chosen = prettyLogSequence(minVal, maxVal, [1, 2, 5]);
    for (const mantissas of [[1, 2, 5], [1, 5], [1]]) {
        const seq = prettyLogSequence(minVal, maxVal, mantissas);
        chosen = seq;
        if (seq.length <= wanted) break;
    }
    if (chosen.length <= wanted) return chosen;
    const thinned = [];
    const last = chosen.length - 1;
    for (let i = 0; i < wanted; i += 1) {
        thinned.push(chosen[Math.round((i * last) / (wanted - 1))]);
    }
    return [...new Set(thinned)];
}

function rampColorsForCount(count) {
    const n = ENERGY_RAMP_COLORS.length;
    if (count >= n) return ENERGY_RAMP_COLORS.slice(0, count);
    if (count <= 1) return [ENERGY_RAMP_COLORS[n - 1]];
    return Array.from({ length: count }, (_, i) => (
        ENERGY_RAMP_COLORS[Math.round((i * (n - 1)) / (count - 1))]
    ));
}

export function buildRelativeEnergyScale(hexes, valueKey = 'annual_energy_kwh') {
    const values = (hexes || [])
        .map((h) => Number(h?.[valueKey]) || 0)
        .filter((v) => v > 0)
        .sort((a, b) => a - b);
    if (!values.length) {
        return {
            maxKwh: 0,
            key: 'empty',
            stops: [],
            legend: [{ label: 'No load', color: '#9ca3af' }],
        };
    }

    const maxV = values[values.length - 1];
    const prettyBreaks = prettyLogBreaks(
        MIN_THRESHOLD_KWH,
        Math.max(maxV, MIN_THRESHOLD_KWH),
        ENERGY_RAMP_COLORS.length - 1
    );
    const cutoffs = [...prettyBreaks].sort((a, b) => b - a);
    if (cutoffs[cutoffs.length - 1] !== MIN_THRESHOLD_KWH) {
        cutoffs.push(MIN_THRESHOLD_KWH);
    }
    cutoffs.push(0);

    const colors = rampColorsForCount(cutoffs.length);
    const stops = cutoffs.map((minKwh, i) => ({
        minKwh,
        color: colors[i].rgba,
        hex: colors[i].hex,
    }));
    const legend = stops.map((stop, i) => {
        const next = i === 0 ? null : stops[i - 1];
        let label;
        if (i === 0) {
            label = `> ${formatEnergyKwh(stop.minKwh)}/yr`;
        } else if (stop.minKwh <= 0) {
            label = `< ${formatEnergyKwh(next.minKwh)}/yr`;
        } else {
            label = `${formatEnergyKwh(stop.minKwh)}–${formatEnergyKwh(next.minKwh)}/yr`;
        }
        return { label, color: stop.hex };
    });
    return {
        maxKwh: maxV,
        key: cutoffs.join(','),
        stops,
        legend,
    };
}

export function getHexColor(annualEnergyKwh, scale) {
    const kwh = Number(annualEnergyKwh) || 0;
    if (!scale?.stops?.length || scale.maxKwh <= 0) return EMPTY_HEX_COLOR;
    for (const stop of scale.stops) {
        if (kwh >= stop.minKwh) return stop.color;
    }
    return scale.stops[scale.stops.length - 1].color;
}
